import { Controller } from "@hotwired/stimulus"

// Saves the proposal builder's quote selection as each box is ticked, and puts
// the returned totals bar and discounts card in place.
//
// The selection saves immediately (rather than with the rest of the form)
// because discounts are validated against the proposal's saved quotes: a
// quote-level discount on a just-ticked quote would otherwise be refused, and a
// discount on a just-unticked one would not show as orphaned. Pricing stays on
// the server (Sales::DiscountedPricing) instead of being re-summed here.
export default class extends Controller {
  static targets = ["quoteCheckbox"]
  static values = {
    url: String,
    debounceMs: { type: Number, default: 250 }
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    if (this.inflight) this.inflight.abort()
  }

  refresh() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.debounceMsValue)
  }

  async save() {
    const body = new FormData()
    body.append("_method", "patch")
    // Blank entry so unticking every quote still saves an (empty) selection.
    body.append("sales_proposal[quote_ids][]", "")
    this.checkedIds().forEach((id) => body.append("sales_proposal[quote_ids][]", id))

    // Supersede any in-flight save so responses cannot arrive out of order and
    // leave stale totals on screen.
    if (this.inflight) this.inflight.abort()
    const ac = new AbortController()
    this.inflight = ac

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body,
        credentials: "same-origin",
        signal: ac.signal
      })

      if (!response.ok) {
        console.error("[proposal-builder] save failed:", response.status)
        return
      }

      const html = await response.text()
      if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
        window.Turbo.renderStreamMessage(html)
      }
    } catch (err) {
      if (err.name === "AbortError") return // superseded by a newer tick
      console.error("[proposal-builder] save error:", err)
    } finally {
      if (this.inflight === ac) this.inflight = null
    }
  }

  checkedIds() {
    return this.quoteCheckboxTargets.filter((el) => el.checked).map((el) => el.value)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
